import { Transition, Dialog } from '@headlessui/react';
import { Fragment, useState, useContext, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/solid';
import mixpanel from 'mixpanel-browser';

import { SidebarItem } from './SidebarItem';
import { StarIcon, UserIcon, ShareIcon, ChainIcon } from '../../Icons';
import { SignIn } from '../SignIn';

import { logout } from '../../helpers/magic';

import { TrialModal } from '../TrialModal';
import { SubscriptionModal } from '../SubscriptionModal';
import { PaywallModal } from '../PaywallModal';
import { UserContext } from '../../context/userContext';
import { Notification } from '../Notification';

import { navigation, faq, assets } from './items';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const Sidebar = ({ isOpen, onClose }: Props) => {
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [isTrialOpen, setTrialOpen] = useState(false);
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [isSubscriptionStatusOpen, setSubscriptionStatusOpen] = useState(false);
  const [isNotificationVisible, setNotificationVisible] = useState(false);

  const { user, setUser } = useContext(UserContext);
  const {
    hasPremium = false,
    isNewSubscriber = true,
    subscriptionEnd = null,
    isCanceled = null,
    plan = null,
  } = user || {};

  const handleAuthButtonClick = async () => {
    if (user) {
      await logout(setUser);
    } else {
      setAuthOpen(true);
    }
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
  };

  const handlePremiumClick = () => {
    if (user && user.email) {
      if (isNewSubscriber) {
        setTrialOpen(true);
        mixpanel.track('Paywall trial', { premium: hasPremium, trial: false });
      } else if (subscriptionEnd && subscriptionEnd > Date.now()) {
        setSubscriptionStatusOpen(true);
      } else {
        mixpanel.track('Paywall', { premium: hasPremium, trial: false });
        setPaywallOpen(true);
      }
    } else {
      setTrialOpen(true);
      mixpanel.track('Paywall trial', { premium: hasPremium, trial: false });
    }
  };

  const handleRenewalClick = () => {
    setSubscriptionStatusOpen(false);
    setPaywallOpen(true);
  };

  const handleCancelClick = () => {
    setUser({ ...user, isCanceled: true });
  };

  const shareButtonClick = () => {
    navigator.clipboard
      .writeText('https://toolsforprocreate.onelink.me/zChQ/web')
      .then(() => setNotificationVisible(true));
  };

  useEffect(() => {
    if (isNotificationVisible) {
      const timer = setTimeout(() => setNotificationVisible(false), 1500);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  return (
    <div>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as='div' className='fixed inset-0 flex z-10' onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter='transition-opacity ease-linear duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-linear duration-300'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-600 bg-opacity-75' />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter='transition ease-in-out duration-300 transform'
            enterFrom='-translate-x-full'
            enterTo='translate-x-0'
            leave='transition ease-in-out duration-300 transform'
            leaveFrom='translate-x-0'
            leaveTo='-translate-x-full'
          >
            <div className='relative flex-1 flex flex-col max-w-[240px] w-full pt-5 pb-4 bg-main-grey-700'>
              <div className='mt-5 flex-1 h-0 overflow-y-auto px-6'>
                {user && user.email ? (
                  <>
                    <p className='truncate mb-2 text-main-blue text-xs'>{user.email}</p>
                    <button
                      type='button'
                      style={{ outline: 'none' }}
                      className={`group flex w-full
              items-center px-2 py-1 
              rounded-md bg-main-grey-500 
              text-xs mb-10 font-medium justify-center cursor-pointer`}
                      onClick={handleAuthButtonClick}
                    >
                      Log out
                    </button>
                  </>
                ) : (
                  <button
                    type='button'
                    style={{ outline: 'none' }}
                    className={`group flex w-full
                items-center 
                px-2 py-1 text-main-blue rounded-md hover:bg-white text-xs mb-10 cursor-pointer mt-1`}
                    onClick={handleAuthButtonClick}
                  >
                    <UserIcon className='mr-2 flex-shrink-0 w-[24px] h-[24px]' aria-hidden='true' />
                    Sign in
                  </button>
                )}

                <nav className='space-y-1 mb-10'>
                  {navigation.map((item) => (
                    <SidebarItem
                      name={item.name}
                      href={item.href}
                      Icon={item.icon}
                      isExternal={item.isExternal}
                      key={item.name}
                    />
                  ))}
                </nav>
                <nav className='space-y-1 mb-10'>
                  <button
                    className={`group flex w-full
                items-center 
                px-2 py-2
                rounded-md
                text-xs
                ${hasPremium && user && user.email ? 'bg-light-blue text-main-blue' : 'bg-main-blue text-white'}
                `}
                    type='button'
                    onClick={handlePremiumClick}
                    style={{ outline: 'none' }}
                  >
                    <StarIcon className='mr-3 flex-shrink-0 w-[18px] h-[18px]' aria-hidden='true' />
                    {hasPremium && user && user.email ? 'You are premium' : 'Premium'}
                    {hasPremium && user && user.email && (
                      <InformationCircleIcon className='h-[14px] w-[14px] text-main-blue ml-1' />
                    )}
                  </button>
                  {faq.map((item) => (
                    <SidebarItem
                      name={item.name}
                      href={item.href}
                      Icon={item.icon}
                      isExternal={item.isExternal}
                      key={item.name}
                    />
                  ))}
                </nav>
                <button
                  type='button'
                  className='group flex items-center px-2 py-2 rounded-md hover:bg-white text-xs w-full'
                  style={{ outline: 'none' }}
                  onClick={shareButtonClick}
                >
                  <ShareIcon className='mr-2 flex-shrink-0 w-[24px] h-[24px]' aria-hidden='true' />
                  Share our app!
                </button>
                <div className='space-y-1'>
                  {assets.map((item) => (
                    <SidebarItem
                      name={item.name}
                      href={item.href}
                      Icon={item.icon}
                      isExternal={item.isExternal}
                      key={item.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Transition.Child>
          <div className='flex-shrink-0 w-14' aria-hidden='true'>
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
          <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
          <TrialModal
            isOpen={isTrialOpen}
            onClose={() => setTrialOpen(false)}
            userId={(user && user.id) || null}
            email={(user && user.email) || null}
          />
          <PaywallModal
            isOpen={isPaywallOpen}
            onClose={() => setPaywallOpen(false)}
            userId={(user && user.id) || null}
            email={(user && user.email) || null}
            hasPremium={hasPremium}
            isTrial={(plan && plan.isTrial) || false}
          />
          {Boolean(user && user.email && subscriptionEnd && plan) && (
            <SubscriptionModal
              isOpen={isSubscriptionStatusOpen}
              onClose={() => setSubscriptionStatusOpen(false)}
              endDate={subscriptionEnd}
              onRenewal={handleRenewalClick}
              email={user.email}
              isCanceled={isCanceled}
              onCancel={handleCancelClick}
              plan={plan}
            />
          )}
          {isNotificationVisible && (
            <Notification icon={<ChainIcon className='w-[20px]' />} text='Share link copied' color='#000000' />
          )}
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export { Sidebar };
